import { bindActionCreators, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ProcessTag, Scribe } from 'scribe-web-shared/types/document';
import scribeDocumentService from 'services/scribeDocumentService';

export type DocumentReviewState = {
  documents: readonly Scribe[];
  aggregates: object;
  processTagFilter?: ProcessTag;
  isLoading: boolean;
  error: object;
  totalDocuments: number;
};

const initialState: DocumentReviewState = {
  documents: undefined,
  processTagFilter: undefined,
  isLoading: false,
  error: undefined,
  aggregates: undefined,
  totalDocuments: undefined
};

export const fetchDocuments = createAsyncThunk(
  'documents/fetch',
  (query: { processTagFilter: string; aggregateBy?: string; skip?: number }) => {
    // @ts-expect-error - Should be solved when migrated to React-Query
    return scribeDocumentService.listWorkspaceWithFolders({ getFirstQuery: '20', ...query });
  }
);

const documentReviewSlice = createSlice({
  name: 'documentReviewSlice',
  initialState,
  reducers: {
    setProcessTagFilter: (state, action: PayloadAction<ProcessTag>) => {
      state.processTagFilter = action.payload;
      state.documents = undefined;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocuments.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.documents === undefined) {
          state.documents = action.payload.current_folder_scribe_documents;
        } else {
          state.documents.push(...action.payload.current_folder_scribe_documents);
        }
        if (action.payload.notreviewed || action.payload.notreviewed === 0) {
          state.aggregates = {
            notreviewed: action.payload.notreviewed,
            bestpractice: action.payload.bestpractice,
            tobeoptimized: action.payload.tobeoptimized
          };
          state.totalDocuments = Object.values(state.aggregates).reduce((total: number, value: number) => total + value, 0);
        }
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.documents = undefined;
        state.error = action.error;
      });
  }
});

export const useDocumentReviewActions = () => {
  const dispatch = useDispatch();

  // TODO: Make this a shared state across the app
  return useMemo(() => bindActionCreators({ ...documentReviewSlice.actions, fetchDocuments }, dispatch), [dispatch]);
};

export default documentReviewSlice.reducer;
